'use client'

import { Tag } from '@snowball-tech/fractal/dist/src/components/Tag'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cj } from '@snowball-tech/fractal/dist/src/styles/helpers'
import { UilArrowRight as RightArrowIcon } from '@tooni/iconscout-unicons-react'
import { useSearchParams } from 'next/navigation'

import type { Edition, MediaUser } from '../../types'

import { Apps } from '../../enums'
import { isValidDate } from '../../helpers/date'
import { getEditionUrl } from '../../helpers/navigation'
import { isUserPaying } from '../../helpers/user'
import useAppName from '../../hooks/useAppName'
import useMediaQuery from '../../hooks/useMediaQuery'
import useNewsletter from '../../hooks/useNewsletter'
import useTranslations from '../../hooks/useTranslations'
import useUrls from '../../hooks/useUrls'
import Card from '../Card'

type Props = {
  edition: Edition
  user: MediaUser | null
  className?: string
  condensed?: boolean
  fullWidth?: boolean
  href?: string
  orientation?: 'horizontal' | 'vertical'
  rootClassName?: string
  variant?: 'brutal' | 'subtle'
  withDescription?: boolean
  withFooter?: boolean
  withNewsletterTag?: boolean
  withPriceTag?: boolean
  withReadMore?: boolean
}
export default function EditionCard({
  className,
  condensed = false,
  edition,
  fullWidth = false,
  href,
  orientation = 'horizontal',
  rootClassName,
  user,
  variant = 'brutal',
  withDescription = true,
  withFooter = true,
  withNewsletterTag = true,
  withPriceTag = true,
  withReadMore = true,
}: Props) {
  const $t = useTranslations()
  const searchParams = useSearchParams()
  const mediaQueries = useMediaQuery()

  const assetsURLs = useUrls()

  const { nameWithIcon: newsletterLabel } = useNewsletter(edition.newsletter, {
    fullQualifiedUrl: false,
  })

  const title = edition.publication?.socialPreview?.title || edition.title || ''
  const description =
    edition.publication?.socialPreview?.subtitle ||
    edition.subtitle ||
    edition.summary ||
    ''

  const imageUrl =
    edition.publication?.socialPreview?.image ||
    edition.newsletter.illustrationUrl ||
    `${assetsURLs.assets}/images/SnowieWelcome.png`

  const { hasPaywall: isPaid } = edition
  const isPaying = isUserPaying(user, searchParams)

  const appName = useAppName()
  const url =
    href ||
    (appName === Apps.Media
      ? `/${getEditionUrl(edition, { fullQualified: false })}`
      : getEditionUrl(edition))

  return (
    <Card
      className={className}
      condensed={condensed}
      fullWidth={fullWidth}
      href={url}
      image={imageUrl}
      orientation={orientation}
      rootClassName={rootClassName}
      variant={variant}
    >
      {(withNewsletterTag || withPriceTag) && (
        <div
          className={cj(
            'flex flex-row flex-wrap ',
            orientation === 'vertical'
              ? withNewsletterTag && withPriceTag
                ? 'justify-between gap-2'
                : 'justify-center'
              : 'gap-1',
            condensed && orientation === 'horizontal' ? '' : 'md:gap-2',
          )}
        >
          {withNewsletterTag && (
            <Tag
              className="line-clamp-1"
              color="white"
              size={withPriceTag ? 's' : 'm'}
            >
              {newsletterLabel}
            </Tag>
          )}

          {withPriceTag && (
            <Tag
              className="line-clamp-1"
              color={isPaid ? 'yellow' : 'purple'}
              label={$t(
                isPaid
                  ? `editions.list.price.paid.${isPaying ? 'unlocked' : 'locked'}`
                  : 'editions.list.price.free',
              )}
              size={withNewsletterTag ? 's' : 'm'}
            />
          )}
        </div>
      )}

      <Typography
        className={cj(
          'line-clamp-2',
          orientation === 'vertical' ? 'md:flex-1' : '',
          condensed ? 'flex-1' : 'md:line-clamp-3',
        )}
        element="h2"
        variant={
          (condensed || mediaQueries.toMd) && orientation === 'horizontal'
            ? 'body-2-median'
            : 'heading-4'
        }
      >
        {title}
      </Typography>

      {withDescription && (
        <Typography
          className={cj(
            'line-clamp-3 flex-1',
            orientation === 'vertical' ? ' md:[flex:unset]' : 'hidden',
            condensed && orientation === 'horizontal'
              ? ''
              : 'md:[display:-webkit-box]',
          )}
          element="h3"
          variant="body-2"
        >
          {description}
        </Typography>
      )}

      {withFooter &&
        (!condensed || orientation === 'vertical' || mediaQueries.fromMd) && (
          <div
            className={cj(
              'flex-row justify-between items-center gap-2',
              orientation === 'horizontal' ? 'hidden md:flex' : 'flex',
            )}
          >
            {isValidDate(edition.publication.releaseDate) && (
              <Typography className="text-grey-30" variant="caption-median">
                {$t('editions.list.published_at', {
                  date: edition.publication.releaseDate,
                })}
              </Typography>
            )}

            {withReadMore && (
              <Typography
                className="flex flex-row items-center no-underline"
                element="div"
                href={url}
                variant="caption-median"
              >
                {$t('editions.list.read_more')}
                <RightArrowIcon />
              </Typography>
            )}
          </div>
        )}
    </Card>
  )
}
